import { createSlice, current, PayloadAction } from '@reduxjs/toolkit';
import initialState from '../initialState';
import { IBuiltCar } from '../../../../../common/model/IBuiltCar';
import { ICar } from '../../model/IVariantDetails';
import { IExteriorColour, IInteriorColour } from '../../../../../common/model/IColours';
import { IVariantListItems } from '../../model/IProductVariantList';
import { IDeal } from '../../../../../common/model/IDeal';
import { ICarDetails } from '../../../../../common/model/ICarDetails';

const carConfigurationReducer = createSlice({
  name: 'CarConfigurationReducer',
  initialState: initialState.carConfigurationInitialState,
  reducers: {
    setVariant: (
      state,
      {
        payload: { variantListItems, variantId },
      }: PayloadAction<{ variantListItems: IVariantListItems; variantId: string }>,
    ) => {
      state.customisedCar.variantId = variantId;
      state.customisedCar.carId = Object.values(variantListItems).find(list => list[variantId])[variantId]?.carId;

      state.customisedCar.accessories = [];
      state.customisedCar.packs = [];
      state.customisedCar.servicePlan = null;
    },
    /**
     * @TODO: Refactor.
     */
    setDataFromDeal: (state, { payload }: PayloadAction<IDeal>) => {
      state.configuration = payload.fullProductConfiguration as unknown as ICarDetails;
      state.customisedCar.exterior = payload.fullProductConfiguration.exteriorColour;
      state.customisedCar.interior = payload.fullProductConfiguration.interiorColour;
    },
    addAccessory: (state, { payload }: PayloadAction<string>) => {
      state.customisedCar.accessories = [...current(state).customisedCar.accessories, payload];
    },
    removeAccessory: (state, { payload }: PayloadAction<string>) => {
      state.customisedCar.accessories = current(state).customisedCar.accessories.filter(
        accessory => accessory !== payload,
      );
    },
    toggleAccessory: (state, { payload }: PayloadAction<string>) => {
      state.customisedCar.accessories = current(state).customisedCar.accessories.filter(
        accessory => accessory !== payload,
      );
    },
    addServicePlan: (state, { payload }: PayloadAction<string>) => {
      state.customisedCar.servicePlan = payload;
    },
    removeServicePlan: state => {
      state.customisedCar.servicePlan = null;
    },
    addAccessoryPack: (state, { payload }: PayloadAction<string>) => {
      state.customisedCar.packs = [...current(state).customisedCar.packs, payload];
    },
    removeAccessoryPack: (state, { payload }: PayloadAction<string>) => {
      state.customisedCar.packs = current(state).customisedCar.packs.filter(pack => pack !== payload);
    },
    setCarConfiguration: (state, { payload: { car } }: PayloadAction<{ car: IBuiltCar }>) => {
      state.customisedCar = {
        ...state.customisedCar,
        accessories: car.accessories,
        carId: car.carId,
        packs: car.packs,
        servicePlan: car.servicePlan,
        // @TODO: This is a temporary solution for better performance. Should be removed
        // with shortening of url.
        variantId: car.carId.split('+').shift(),
      };
    },
    setShouldDisplayInterior: (state, { payload }: PayloadAction<boolean>) => {
      state.customisedCar.shouldDisplayInterior = payload;
    },
    setExteriorColour: (
      state,
      {
        payload: { exterior, variantDetailsCars },
      }: PayloadAction<{ exterior: IExteriorColour; variantDetailsCars: ICar }>,
    ) => {
      state.customisedCar.exterior = exterior;
      state.customisedCar.carId = variantDetailsCars[exterior.id][current(state).customisedCar.interior.id];
    },
    setInteriorColour: (
      state,
      {
        payload: { interior, variantDetailsCars, shouldDisplayInterior },
      }: PayloadAction<{
        interior: IInteriorColour;
        variantDetailsCars: ICar;
        shouldDisplayInterior?: boolean | undefined;
      }>,
    ) => {
      state.customisedCar.interior = interior;
      state.customisedCar.carId = variantDetailsCars[current(state).customisedCar.exterior.id][interior.id];

      if (shouldDisplayInterior !== undefined) {
        carConfigurationReducer.caseReducers.setShouldDisplayInterior(state, {
          payload: true,
          type: 'setShouldDisplayInterior',
        });
      }
    },
    resetCustomiseCar: state => {
      state.customisedCar = null;
    },
  },
});

export const carConfigurationActions = carConfigurationReducer.actions;

export default carConfigurationReducer.reducer;
